import React, { useContext } from 'react'
import styled from 'styled-components'
import css from '@styled-system/css'

import { Flex, P, Grid } from 'ui/bend'
import { ResponsiveSVG } from 'ui/acro'
import { AlomovesIconSVG } from 'images/latest'
import { useUserContext } from 'context'
import { storeEventInLocalStorage } from 'gtm/pushEvents'
import CommonLink from 'components_v2/CommonLink'
import { useInRouterContext } from 'react-router-dom'
import { HomeContext } from 'components_v2/page_context'

export const CardCTAText = styled(P)(() =>
  css({
    fontSize: '18px',
    letterSpacing: '0.9px',
    textTransform: 'uppercase',
    marginTop: '7px',
    fontWeight: '500',
    lineHeight: '24px',
    textAlign: 'center'
  })
)

const ItemContainer = styled(Flex)`
  width: 100%;
  max-width: 100%;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding-top: 56.25%;
  position: relative;

  @media (max-width: 1024px) {
    padding-top: 100%;
  }
`

const ButtonContainer = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 0 16px;
  position: absolute;
  margin-top: -56.25%;
  width: fit-content;

  @media (max-width: 1024px) {
    margin-top: -100%;
    padding: 0 8px;
  }
`

const ButtonWrapper = styled.div`
  text-align: center;
`
const StyledButton = styled.button`
  display: inline-block;
  padding: 10px 20px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 360px;
  background: #000;
  box-sizing: border-box;
  color: #fff;
  border: none;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  margin-top: 16px;
  white-space: nowrap;

  @media (max-width: 1024px) {
    font-size: 14px;
    letter-spacing: 0.7px;
    border: 2px solid black;
    padding: 2px 10px;
  }
`
const CardText = styled.div`
  color: #636363;
  text-align: center;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.352px;
  min-height: 50px;

  @media (max-width: 1024px) {
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.308px;
  }
`

const CardSummarySection = styled(Grid)`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  p {
    font-size: 16px;
  }
  a {
    flex: 1 0 40%;
  }
  @media screen and (min-width: 764px) {
    gap: 20px;
    p {
      font-size: 18px;
    }
    a {
      flex: 1 0 20%;
    }
  }

  .no-thumbnail {
    position: absolute;
    margin-top: -56.25%;
    width: fit-content;
    height: 70%;
  }
  .pre {
    display: inline-flex;
  }
`

const MyPraticeCard = ({
  data,
  keyName,
  ctaText,
  description,
  zeroLink,
  nonZeroLink
}) => {
  const homeContext = useContext(HomeContext)
  const setIsCreatePlayList = homeContext?.setIsCreatePlayList
  const textObject = {
    completedClasses: 'Completed',
    playlists: 'My Playlists',
    savedClasses: 'Saved Classes',
    savedSeries: 'Saved Programs'
  }

  const linkObject = {
    completedClasses: 'completed',
    playlists: 'playlists',
    savedClasses: 'classes',
    savedSeries: 'series'
  }

  const count = data?.[keyName]?.count
  const lastThumbnail = data?.[keyName]?.lastThumbnail
  const insideRouter = useInRouterContext()

  const handleClick = (tab) => {
    const eventMap = {
      series: 'saved_series_click',
      classes: 'saved_classes_click',
      completed: 'completed_classes_click',
      playlists: 'my_playlists_click'
    }

    if (count > 0) {
      if (insideRouter) {
        window.dataLayer ||= []
        window.dataLayer.push({ event: eventMap[tab], location: 'homepage' })
        return
      }
      storeEventInLocalStorage({ event: eventMap[tab], location: 'homepage' })
    }
  }

  const thumbnailPresent = count > 0 && !!lastThumbnail

  return (
    <CommonLink
      {...(!(keyName === 'playlists' && !Boolean(count > 0)) && {
        to: count > 0 ? nonZeroLink : zeroLink
      })}
      onClick={(e) => {
        if (keyName === 'playlists' && !Boolean(count > 0)) {
          e.preventDefault()
          setIsCreatePlayList && setIsCreatePlayList(true)
        } else {
          handleClick(linkObject?.[keyName]?.toLowerCase())
        }
      }}
      className={keyName}
    >
      <ItemContainer
        aspectRatio='16/9'
        {...(thumbnailPresent
          ? {
              backgroundImage: `url(${lastThumbnail})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }
          : {
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative'
            })}
        {...(count > 0 && !lastThumbnail
          ? { backgroundColor: '#F2F2F2' }
          : { backgroundColor: '#FFF' })}
      >
        {!lastThumbnail && count > 0 && (
          <ResponsiveSVG
            width={['68px', '68px', '130px', '130px']}
            height={['68px', '68px', '130px', '130px']}
            SVG={AlomovesIconSVG}
            className='no-thumbnail'
          />
        )}
        {!Boolean(count > 0) && (
          <ButtonContainer>
            <CardText dangerouslySetInnerHTML={{ __html: description }} />
            <ButtonWrapper>
              <StyledButton>{ctaText}</StyledButton>
            </ButtonWrapper>
          </ButtonContainer>
        )}
      </ItemContainer>
      <CardCTAText>{`${textObject[keyName]} (${count})`}</CardCTAText>
    </CommonLink>
  )
}

const practiceCards = [
  {
    keyName: 'completedClasses',
    ctaText: 'Start a Class',
    description:
      'Your completed classes tracked here. <span class="pre">5-10</span> minutes is all you need.',
    zeroLink: '/class-finder?order=date',
    nonZeroLink: '/my/practice?tab=completed'
  },
  {
    keyName: 'savedClasses',
    ctaText: 'Browse Classes',
    description:
      'See something you like? Save classes to build your go-to list.',
    zeroLink: '/class-finder?order=date',
    nonZeroLink: '/my/practice?tab=classes'
  },
  {
    keyName: 'savedSeries',
    ctaText: 'Explore Programs',
    description:
      'Prefer a plan to follow? Save a program to guide you step by step.',
    zeroLink: '/explore/filter?order=date',
    nonZeroLink: '/my/practice?tab=series'
  },
  {
    keyName: 'playlists',
    ctaText: 'Build a Playlist',
    description:
      'Create custom class mixes for your routines, goals or favorites.',
    zeroLink: '#',
    nonZeroLink: '/my/practice?tab=playlists'
  }
]

export const MyPraticeCards = ({ setIsCreatePlayList }) => {
  const { user } = useUserContext()
  return (
    <CardSummarySection>
      {practiceCards.map((card) => (
        <MyPraticeCard
          key={card.keyName}
          ctaText={card.ctaText}
          keyName={card.keyName}
          description={card.description}
          zeroLink={card.zeroLink}
          nonZeroLink={card.nonZeroLink}
          data={user?.myPracticeSnapshot?.details}
          setIsCreatePlayList={setIsCreatePlayList}
        />
      ))}
    </CardSummarySection>
  )
}
